import { TaiXiuBetType, normalizeTaiXiuTicketBetType } from '@/utils/index.model';

/*
 * How a Tài Xỉu bet's market is badged in the bet lists (the rail's CURRENT
 * and the bet history), from the ticket's `bet_type` (either numbering, see
 * `normalizeTaiXiuTicketBetType`) + `selection`:
 *  - total (4..17): the three-dot mark and the total (Figma 457:7859);
 *  - single / double / triple: that face, once, twice or three times.
 * Tài / Xỉu / Lẻ / Chẵn (and every cockfight bet) are plain text badges, so
 * they get `null` and keep the caller's own label.
 */
export type TaiXiuIconMarket = { kind: 'total'; value: number } | { kind: 'dice'; faces: number[] };

export const taiXiuIconMarket = (betType: unknown, selection: unknown): TaiXiuIconMarket | null => {
  const type = normalizeTaiXiuTicketBetType(betType, selection);
  if (type === undefined) return null;
  if (type >= TaiXiuBetType.total4 && type <= TaiXiuBetType.total17) {
    return { kind: 'total', value: type - TaiXiuBetType.total4 + 4 };
  }

  const face = Number(selection);
  if (!Number.isInteger(face) || face < 1 || face > 6) return null;
  if (type === TaiXiuBetType.triple) return { kind: 'dice', faces: [face, face, face] };
  if (type === TaiXiuBetType.single) return { kind: 'dice', faces: [face] };
  if (type === TaiXiuBetType.double) return { kind: 'dice', faces: [face, face] };
  return null;
};

/*
 * Tài Xỉu odds come from the backend (bet-type-odds, tickets) as Euro odds --
 * the stake included, e.g. 1.97 -- but are shown as game odds, the profit per
 * unit staked: `1 : 0.97`. `null` for a missing / non-numeric value.
 */
export const taiXiuGameOdds = (euroOdds: unknown): number | null => {
  if (euroOdds === undefined || euroOdds === null || euroOdds === '') return null;
  const value = Number(euroOdds);
  return Number.isFinite(value) && value > 1 ? value - 1 : null;
};

export const formatTaiXiuOdds = (gameOdds: number): string => `1 : ${parseFloat(gameOdds.toFixed(4))}`;

// Game odds of a total (4..17) before the backend's arrive; 4/17, 5/16, ... pay alike.
const TOTAL_GAME_ODDS: { [sum: number]: number } = { 4: 30, 5: 18, 6: 14, 7: 12, 8: 8, 9: 6, 10: 6 };
export const fallbackTotalGameOdds = (sum: number): number => TOTAL_GAME_ODDS[sum <= 10 ? sum : 21 - sum] || 0;

/*
 * `taiXiuIconMarket`, plus a total read from the selection (4..17) when the
 * bet type does not match it or is missing -- as a selection of a
 * multi-selection ticket can come back.
 */
export const taiXiuSelectionMarket = (betType: unknown, selection: unknown): TaiXiuIconMarket | null => {
  const market = taiXiuIconMarket(betType, selection);
  if (market) return market;
  const total = Number(selection);
  return Number.isInteger(total) && total >= 4 && total <= 17 ? { kind: 'total', value: total } : null;
};
